import React, { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import Alert from "@mui/material/Alert";

import AppBar from "decodable-ui/lib/components/AppBar";

import { useAccounts, useActiveAccount } from "../accounts/hooks/useAccounts";
import config from "../config";
import { useAuth } from "../hooks/useAuth";
import { rootUrl, auth0LoginOptions } from "../lib/util";
import { OpenCommandPaletteButton } from "./CommandPalette";
import { DataPlaneSetupAlert } from "./DataPlaneSetupAlert";
import { Stack } from "@mui/system";
import { useAppEventDispatch } from "../hooks/useAppEvents";
import useDelay from "decodable-ui/lib/hooks/useDelay";

declare global {
  var Osano: {
    cm: {
      showDrawer(id: string): void;
    };
  };
}

function openCookieSettings() {
  // https://docs.osano.com/hiding-the-cookie-widget
  globalThis.Osano.cm.showDrawer("osano-cm-dom-info-dialog-open");
}

export type TopBarProps = {
  isLoading?: boolean;
  renderNav?: () => ReactNode;
  disableLogo?: boolean;
};

export const TopBar = React.memo<TopBarProps>(function TopBar({
  isLoading = false,
  renderNav,
  disableLogo,
}) {
  const {
    loginWithRedirect,
    logout,
    user,
    isLoading: isAuthLoading,
  } = useAuth();
  const navigate = useNavigate();
  const { accounts } = useAccounts();
  const activeAccountCtx = useActiveAccount();
  const dispatch = useAppEventDispatch();
  const { delayPassed: loadingDelayPassed } = useDelay(300);

  const login = () => loginWithRedirect(auth0LoginOptions());
  const logoutToRoot = () => {
    dispatch("logout");
    logout({ logoutParams: { returnTo: rootUrl(window.location) } });
  };

  return isAuthLoading ? (
    <AppBar disableLogo={disableLogo} anonymous />
  ) : (
    <>
      {config.DECODABLE_ENV == "local" && (
        <DevModeIndicator devUrl={config.DECODABLE_ACCOUNT_API_URL_TEMPLATE} />
      )}
      <DataPlaneSetupAlert activeAccountCtx={activeAccountCtx} />
      <AppBar
        disableLogo={disableLogo}
        user={user}
        userLoading={isAuthLoading}
        activeAccount={activeAccountCtx.activeAccount}
        homeHref={
          activeAccountCtx.activeAccount
            ? `/${activeAccountCtx.activeAccount.name}`
            : "/"
        }
        onLogIn={login}
        renderNav={renderNav}
        renderActions={() => (
          <Stack direction="row" spacing={1} alignItems="center">
            {activeAccountCtx.activeAccount ? (
              <OpenCommandPaletteButton />
            ) : null}
          </Stack>
        )}
        isLoading={loadingDelayPassed && isLoading}
        accountSwitcherPopoverProps={{
          accountInfo: activeAccountCtx?.accountInfo,
          accounts,
          onLogOut: logoutToRoot,
          onManageAccount: () =>
            navigate(`/${activeAccountCtx.activeAccount.name}/account`),
          onChangeAccount: (account) => navigate(`/${account.name}`),
          onNewAccount: () => navigate(`/-/accounts/create`),
          onOpenCookieSettings: openCookieSettings,
        }}
      />
    </>
  );
});

TopBar.displayName == "TopBar";

export default TopBar;

function DevModeIndicator({ devUrl }) {
  if (!devUrl) {
    return null;
  }
  return (
    <Alert
      severity="warning"
      className="dev-url"
      sx={{ position: "fixed", bottom: 0, right: 0 }}
    >
      Using API URL: {devUrl}
    </Alert>
  );
}
